// SPDX-License-Identifier: AGPL-3.0-or-later

import * as FavoriteMemeCommands from '@app/features/expressions/commands/FavoriteMemeCommands';
import {AddFavoriteMemeModal} from '@app/features/expressions/components/modals/AddFavoriteMemeModal';
import FavoriteMemes from '@app/features/expressions/state/FavoriteMemes';
import * as FavoriteMemeUtils from '@app/features/expressions/utils/FavoriteMemeUtils';
import {EditAltTextModal} from '@app/features/messaging/components/modals/EditAltTextModal';
import type {Message} from '@app/features/messaging/models/Message';
import {createSaveHandler} from '@app/features/messaging/utils/FileDownloadUtils';
import {buildMediaProxyURL, stripMediaProxyParams} from '@app/features/messaging/utils/MediaProxyUtils';
import Permission from '@app/features/permissions/state/Permission';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {
	CopyLinkIcon,
	CopyMediaIcon,
	FavoriteIcon,
	OpenMediaLinkIcon,
	SaveMediaIcon,
} from '@app/features/ui/action_menu/ContextMenuIcons';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import type {MenuGroupType} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import {getElectronAPI, openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import Users from '@app/features/user/state/Users';
import type {ClipboardWriteFileMediaType} from '@app/types/electron.d';
import {Permissions} from '@app/vendor/constants/ChannelConstants';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {PencilSimpleIcon} from '@phosphor-icons/react';
import {autorun} from 'mobx';
import {useCallback, useMemo, useSyncExternalStore} from 'react';

const logger = new Logger('MediaMenuData');

export type MediaType = 'image' | 'gif' | 'gifv' | 'video' | 'audio' | 'file';
async function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
	return await new Promise<Blob>((resolve, reject) => {
		canvas.toBlob((blob) => {
			if (!blob) {
				reject(new Error('Failed to encode PNG'));
				return;
			}
			resolve(blob);
		}, 'image/png');
	});
}
async function createImageBitmapWithOrientation(blob: Blob): Promise<ImageBitmap> {
	try {
		return await createImageBitmap(blob, {imageOrientation: 'from-image'});
	} catch {
		return await createImageBitmap(blob);
	}
}
async function convertImageBlobToPng(blob: Blob): Promise<Blob> {
	if (blob.type === 'image/png') return blob;
	if (blob.size === 0) {
		throw new Error('Image blob is empty');
	}
	const imageBitmap = await createImageBitmapWithOrientation(blob);
	try {
		if (typeof OffscreenCanvas !== 'undefined') {
			const canvas = new OffscreenCanvas(imageBitmap.width, imageBitmap.height);
			const ctx = canvas.getContext('2d');
			if (!ctx) {
				throw new Error('OffscreenCanvas 2D context is unavailable');
			}
			ctx.drawImage(imageBitmap, 0, 0);
			if ('convertToBlob' in canvas) {
				return await canvas.convertToBlob({type: 'image/png'});
			}
		}
		const canvas = document.createElement('canvas');
		canvas.width = imageBitmap.width;
		canvas.height = imageBitmap.height;
		const ctx = canvas.getContext('2d');
		if (!ctx) {
			throw new Error('Canvas 2D context is unavailable');
		}
		ctx.drawImage(imageBitmap, 0, 0);
		return await canvasToPngBlob(canvas);
	} finally {
		imageBitmap.close?.();
	}
}
async function fetchFirstPngBlob(urls: Array<string>): Promise<Blob> {
	let lastError: unknown = null;
	for (const url of urls) {
		try {
			const response = await fetch(url);
			if (!response.ok) {
				throw new Error(`Unexpected response status ${response.status} for ${url}`);
			}
			const blob = await response.blob();
			return await convertImageBlobToPng(blob);
		} catch (error) {
			lastError = error;
		}
	}
	throw lastError ?? new Error('Failed to fetch and convert image');
}
function getClipboardFileMediaType(type: MediaType): ClipboardWriteFileMediaType | null {
	switch (type) {
		case 'gif':
			return 'gif';
		case 'gifv':
		case 'video':
			return 'video';
		case 'audio':
			return 'audio';
		default:
			return null;
	}
}
export interface MediaMenuDataOptions {
	onClose: () => void;
}
export interface MediaMenuDataProps {
	message: Message;
	originalSrc: string;
	proxyURL?: string;
	type: MediaType;
	contentHash?: string | null;
	attachmentId?: string;
	embedIndex?: number;
	defaultName?: string;
	defaultAltText?: string;
	snapshotIndex?: number;
}
export interface MediaMenuData {
	groups: Array<MenuGroupType>;
	handlers: MediaMenuHandlers;
	state: MediaMenuState;
}
export interface MediaMenuHandlers {
	handleAddToFavorites: () => void;
	handleRemoveFromFavorites: () => Promise<void>;
	handleEditAltText: () => void;
	handleCopyMedia: () => Promise<void>;
	handleSaveMedia: () => void;
	handleCopyLink: () => Promise<void>;
	handleOpenLink: () => void;
	handleCopyAttachmentId: () => Promise<void>;
	canEditAltText: boolean;
	canCopyAttachmentId: boolean;
	copyLinkLabel: string;
	openLinkLabel: string;
}
export const mediaMenuItemIds = {
	copy: 'media-copy',
	save: 'media-save',
	copyLink: 'media-copy-link',
	openLink: 'media-open-link',
	favorite: 'media-favorite',
	editAltText: 'media-edit-alt-text',
	copyAttachmentId: 'media-copy-attachment-id',
} as const;
export interface MediaMenuState {
	isFavorited: boolean;
	copyLabel: string;
	saveLabel: string;
}
function getCopyLabel(type: MediaType, i18n: I18n): string {
	switch (type) {
		case 'image':
			return i18n._(msg`Copy Image`);
		case 'gif':
		case 'gifv':
			return i18n._(msg`Copy GIF`);
		case 'video':
			return i18n._(msg`Copy Video`);
		case 'audio':
			return i18n._(msg`Copy Audio`);
		case 'file':
			return i18n._(msg`Copy Link`);
		default:
			return i18n._(msg`Copy Media`);
	}
}
function getCopyLinkLabel(type: MediaType, i18n: I18n): string {
	switch (type) {
		case 'image':
			return i18n._(msg`Copy Image Link`);
		case 'gif':
		case 'gifv':
			return i18n._(msg`Copy GIF Link`);
		case 'video':
			return i18n._(msg`Copy Video Link`);
		case 'audio':
			return i18n._(msg`Copy Audio Link`);
		case 'file':
			return i18n._(msg`Copy File Link`);
		default:
			return i18n._(msg`Copy Link`);
	}
}
function getOpenLinkLabel(type: MediaType, i18n: I18n): string {
	switch (type) {
		case 'image':
			return i18n._(msg`Open Image Link`);
		case 'gif':
		case 'gifv':
			return i18n._(msg`Open GIF Link`);
		case 'video':
			return i18n._(msg`Open Video Link`);
		case 'audio':
			return i18n._(msg`Open Audio Link`);
		case 'file':
			return i18n._(msg`Open File Link`);
		default:
			return i18n._(msg`Open Link`);
	}
}
function getSaveLabel(type: MediaType, i18n: I18n): string {
	switch (type) {
		case 'image':
			return i18n._(msg`Save Image`);
		case 'gif':
		case 'gifv':
			return i18n._(msg`Save GIF`);
		case 'video':
			return i18n._(msg`Save Video`);
		case 'audio':
			return i18n._(msg`Save Audio`);
		case 'file':
			return i18n._(msg`Save File`);
		default:
			return i18n._(msg`Save Media`);
	}
}
export function useMediaMenuData(props: MediaMenuDataProps, options: MediaMenuDataOptions): MediaMenuData {
	const {i18n} = useLingui();
	const {onClose} = options;
	const {
		message,
		originalSrc,
		proxyURL,
		type,
		contentHash,
		attachmentId,
		embedIndex,
		defaultName,
		defaultAltText,
		snapshotIndex,
	} = props;
	const snapshotAttachments =
		snapshotIndex !== undefined ? (message.messageSnapshots?.[snapshotIndex]?.attachments ?? []) : null;
	const memes = useSyncExternalStore(
		(listener) => {
			const dispose = autorun(listener);
			return () => dispose();
		},
		() => FavoriteMemes.memes,
	);
	const isFavorited = contentHash ? memes.some((meme) => meme.contentHash === contentHash) : false;
	const currentUserId = Users.currentUserId;
	const canManageMessages = Permission.can(Permissions.MANAGE_MESSAGES, {channelId: message.channelId});
	const canEditAltText = useMemo(() => {
		if (!attachmentId) return false;
		const attachmentSource = snapshotAttachments ?? message.attachments;
		const attachment = attachmentSource.find((att) => att.id === attachmentId);
		const mimeType = attachment?.content_type?.toLowerCase() ?? '';
		const canEditMedia = mimeType.startsWith('image/') || mimeType.startsWith('video/');
		if (!canEditMedia) return false;
		const isMessageAuthor = currentUserId === message.author?.id;
		return isMessageAuthor || canManageMessages;
	}, [attachmentId, canManageMessages, currentUserId, message, snapshotAttachments]);
	const handleAddToFavorites = useCallback(() => {
		ModalCommands.push(
			modal(() => (
				<AddFavoriteMemeModal
					channelId={message.channelId}
					messageId={message.id}
					attachmentId={attachmentId}
					embedIndex={embedIndex}
					defaultName={
						defaultName ||
						FavoriteMemeUtils.deriveDefaultNameFromEmbedMedia(i18n, {
							url: originalSrc,
							proxy_url: originalSrc,
							flags: 0,
						})
					}
					defaultAltText={defaultAltText}
				/>
			)),
		);
		onClose();
	}, [message, attachmentId, embedIndex, defaultName, defaultAltText, originalSrc, onClose, i18n]);
	const handleRemoveFromFavorites = useCallback(async () => {
		if (!contentHash) return;
		const meme = memes.find((m) => m.contentHash === contentHash);
		if (!meme) return;
		await FavoriteMemeCommands.deleteFavoriteMeme(i18n, meme.id);
		onClose();
	}, [contentHash, memes, onClose, i18n]);
	const handleEditAltText = useCallback(() => {
		if (!canEditAltText || !attachmentId) return;
		const attachmentSource = snapshotAttachments ?? message.attachments;
		const attachment = attachmentSource.find((att) => att.id === attachmentId);
		const currentDescription = attachment?.description ?? null;
		ModalCommands.push(
			modal(() => (
				<EditAltTextModal
					message={message}
					attachmentId={attachmentId}
					currentDescription={currentDescription}
					snapshotIndex={snapshotIndex}
					onClose={() => ModalCommands.pop()}
				/>
			)),
		);
		onClose();
	}, [canEditAltText, message, attachmentId, onClose, snapshotAttachments, snapshotIndex]);
	const handleCopyMedia = useCallback(async () => {
		if (!originalSrc) {
			ToastCommands.createToast({
				type: 'error',
				children: i18n._(msg`Attachment is expired or unavailable`),
			});
			onClose();
			return;
		}
		if (type === 'file') {
			await TextCopyCommands.copy(i18n, originalSrc, true);
			ToastCommands.createToast({type: 'success', children: i18n._(msg`Link copied to clipboard`)});
			onClose();
			return;
		}
		const baseProxyURL = proxyURL ? stripMediaProxyParams(proxyURL) : null;
		const clipboardFileMediaType = getClipboardFileMediaType(type);
		if (clipboardFileMediaType) {
			const electronApi = getElectronAPI();
			if (!electronApi?.clipboardWriteFile) {
				await TextCopyCommands.copy(i18n, originalSrc, true);
				ToastCommands.createToast({type: 'success', children: i18n._(msg`URL copied to clipboard`)});
				onClose();
				return;
			}
			let toastId: string | null = null;
			try {
				toastId = ToastCommands.createToast({
					type: 'info',
					children: i18n._(msg`Loading...`),
					timeout: 0,
				});
				const result = await electronApi.clipboardWriteFile({
					url: baseProxyURL || originalSrc,
					suggestedName: defaultName,
					mediaType: clipboardFileMediaType,
				});
				if (!result.success) {
					throw new Error(result.error ?? 'Clipboard file write failed');
				}
				if (toastId) ToastCommands.destroyToast(toastId);
				ToastCommands.createToast({type: 'success', children: i18n._(msg`Copied to clipboard`)});
			} catch (error) {
				logger.error('Failed to copy media to clipboard:', error);
				if (toastId) ToastCommands.destroyToast(toastId);
				ToastCommands.createToast({type: 'error', children: i18n._(msg`Failed to copy to clipboard`)});
			}
			onClose();
			return;
		}
		const urlsToTry: Array<string> = [];
		if (baseProxyURL) urlsToTry.push(buildMediaProxyURL(baseProxyURL, {format: 'png'}));
		if (baseProxyURL) urlsToTry.push(baseProxyURL);
		urlsToTry.push(originalSrc);
		let toastId: string | null = null;
		try {
			toastId = ToastCommands.createToast({
				type: 'info',
				children: i18n._(msg`Copying image...`),
				timeout: 0,
			});
			if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
				throw new Error('Clipboard image write is unavailable');
			}
			await navigator.clipboard.write([
				new ClipboardItem({
					'image/png': fetchFirstPngBlob(urlsToTry),
				}),
			]);
			if (toastId) ToastCommands.destroyToast(toastId);
			ToastCommands.createToast({
				type: 'success',
				children: i18n._(msg`Image copied to clipboard`),
			});
			onClose();
		} catch (error) {
			logger.error('Failed to copy image to clipboard:', error);
			if (toastId) ToastCommands.destroyToast(toastId);
			await TextCopyCommands.copy(i18n, originalSrc, true);
			ToastCommands.createToast({
				type: 'success',
				children: i18n._(msg`URL copied to clipboard`),
			});
			onClose();
		}
	}, [originalSrc, proxyURL, type, onClose, i18n, defaultName]);
	const handleSaveMedia = useCallback(() => {
		if (!originalSrc) {
			ToastCommands.createToast({
				type: 'error',
				children: i18n._(msg`Attachment is expired or unavailable`),
			});
			onClose();
			return;
		}
		const mediaType: 'image' | 'video' | 'audio' | 'file' = (() => {
			if (type === 'video' || type === 'gifv') return 'video';
			if (type === 'audio') return 'audio';
			if (type === 'file') return 'file';
			return 'image';
		})();
		const baseProxyURL = proxyURL ? stripMediaProxyParams(proxyURL) : null;
		const urlToSave = baseProxyURL || originalSrc;
		createSaveHandler(urlToSave, mediaType)();
		onClose();
	}, [originalSrc, proxyURL, type, onClose, i18n]);
	const handleCopyLink = useCallback(async () => {
		if (!originalSrc) {
			ToastCommands.createToast({
				type: 'error',
				children: i18n._(msg`Attachment is expired or unavailable`),
			});
			onClose();
			return;
		}
		await TextCopyCommands.copy(i18n, originalSrc, true);
		ToastCommands.createToast({
			type: 'success',
			children: i18n._(msg`Link copied to clipboard`),
		});
		onClose();
	}, [originalSrc, onClose, i18n]);
	const handleOpenLink = useCallback(() => {
		if (!originalSrc) {
			ToastCommands.createToast({
				type: 'error',
				children: i18n._(msg`Attachment is expired or unavailable`),
			});
			onClose();
			return;
		}
		void openExternalUrl(originalSrc);
		onClose();
	}, [originalSrc, onClose, i18n]);
	const handleCopyAttachmentId = useCallback(async () => {
		if (!attachmentId) return;
		await TextCopyCommands.copy(i18n, attachmentId, true);
		ToastCommands.createToast({
			type: 'success',
			children: i18n._(msg`Attachment ID copied to clipboard`),
		});
		onClose();
	}, [attachmentId, onClose, i18n]);
	const copyLabel = useMemo(() => getCopyLabel(type, i18n), [type, i18n]);
	const saveLabel = useMemo(() => getSaveLabel(type, i18n), [type, i18n]);
	const copyLinkLabel = useMemo(() => getCopyLinkLabel(type, i18n), [type, i18n]);
	const openLinkLabel = useMemo(() => getOpenLinkLabel(type, i18n), [type, i18n]);
	const handlers: MediaMenuHandlers = useMemo(
		() => ({
			handleAddToFavorites,
			handleRemoveFromFavorites,
			handleEditAltText,
			handleCopyMedia,
			handleSaveMedia,
			handleCopyLink,
			handleOpenLink,
			handleCopyAttachmentId,
			canEditAltText,
			canCopyAttachmentId: Boolean(attachmentId),
			copyLinkLabel,
			openLinkLabel,
		}),
		[
			handleAddToFavorites,
			handleRemoveFromFavorites,
			handleEditAltText,
			handleCopyMedia,
			handleSaveMedia,
			handleCopyLink,
			handleOpenLink,
			handleCopyAttachmentId,
			canEditAltText,
			attachmentId,
			copyLinkLabel,
			openLinkLabel,
		],
	);
	const state: MediaMenuState = useMemo(
		() => ({
			isFavorited,
			copyLabel,
			saveLabel,
		}),
		[isFavorited, copyLabel, saveLabel],
	);
	const groups = useMemo(() => {
		const result: Array<MenuGroupType> = [];
		result.push({
			items: [
				isFavorited
					? {
							id: mediaMenuItemIds.favorite,
							icon: <FavoriteIcon filled size={20} />,
							label: i18n._(msg`Remove from Favorites`),
							onClick: handleRemoveFromFavorites,
						}
					: {
							id: mediaMenuItemIds.favorite,
							icon: <FavoriteIcon size={20} />,
							label: i18n._(msg`Add to Favorites`),
							onClick: handleAddToFavorites,
						},
			],
		});
		if (canEditAltText) {
			result.push({
				items: [
					{
						id: mediaMenuItemIds.editAltText,
						icon: <PencilSimpleIcon size={20} />,
						label: i18n._(msg`Edit Alt Text`),
						onClick: handleEditAltText,
					},
				],
			});
		}
		result.push({
			items: [
				{
					id: mediaMenuItemIds.copy,
					icon: <CopyMediaIcon size={20} />,
					label: copyLabel,
					onClick: handleCopyMedia,
				},
				{
					id: mediaMenuItemIds.save,
					icon: <SaveMediaIcon size={20} />,
					label: saveLabel,
					onClick: handleSaveMedia,
				},
				...(type === 'file'
					? []
					: [
							{
								id: mediaMenuItemIds.copyLink,
								icon: <CopyLinkIcon size={20} />,
								label: copyLinkLabel,
								onClick: handleCopyLink,
							},
						]),
				{
					id: mediaMenuItemIds.openLink,
					icon: <OpenMediaLinkIcon size={20} />,
					label: openLinkLabel,
					onClick: handleOpenLink,
				},
			],
		});
		return result;
	}, [
		isFavorited,
		copyLabel,
		saveLabel,
		copyLinkLabel,
		openLinkLabel,
		type,
		message,
		handleAddToFavorites,
		handleRemoveFromFavorites,
		handleEditAltText,
		handleCopyMedia,
		handleSaveMedia,
		handleCopyLink,
		handleOpenLink,
		canEditAltText,
		i18n,
	]);
	return {
		groups,
		handlers,
		state,
	};
}
